import { AggregatedSessionV2 } from "@global/types/session";
import dayjs from "dayjs";
import {
  KPIMetric,
  Segment,
  SegmentFilter,
  TimeSeriesDataPoint,
} from "../../types/analytics";
import { WhatChangedItem } from "./types";

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/** Get page flow from pageSessions */
function getPageFlow(session: AggregatedSessionV2): string[] {
  return session.pageSessions.map((ps) => ps.pagePath);
}

/** Get page views count (unique pages) from pageSessions */
function getPageViewsCount(session: AggregatedSessionV2): number {
  const pages = new Set(session.pageSessions.map((ps) => ps.pagePath));
  return pages.size;
}

/** Get session duration in seconds */
function getSessionDuration(session: AggregatedSessionV2): number {
  return (session.sessionEnd - session.sessionStart) / 1000;
}

function matchNumericWithFilter(
  value: number,
  operator: string,
  filterValues: string[]
): boolean {
  const numValue = parseFloat(filterValues[0]);
  if (isNaN(numValue) && operator !== "between") return true;

  switch (operator) {
    case "equals":
      return value === numValue;
    case "not_equals":
      return value !== numValue;
    case "greater_than":
      return value > numValue;
    case "greater_than_or_equals":
      return value >= numValue;
    case "less_than":
      return value < numValue;
    case "less_than_or_equals":
      return value <= numValue;
    case "between": {
      const min = parseFloat(filterValues[0]);
      const max = parseFloat(filterValues[1]);
      if (isNaN(min) || isNaN(max)) return true;
      return value >= min && value <= max;
    }
    default:
      return true;
  }
}

function matchDateWithFilter(
  timestamp: number,
  operator: string,
  filterValues: string[]
): boolean {
  const sessionDate = dayjs(timestamp);

  switch (operator) {
    case "equals": {
      const targetDate = dayjs(filterValues[0]);
      if (!targetDate.isValid()) return true;
      return sessionDate.isSame(targetDate, "day");
    }
    case "not_equals": {
      const targetDate = dayjs(filterValues[0]);
      if (!targetDate.isValid()) return true;
      return !sessionDate.isSame(targetDate, "day");
    }
    case "before": {
      const targetDate = dayjs(filterValues[0]);
      if (!targetDate.isValid()) return true;
      return sessionDate.isBefore(targetDate);
    }
    case "after": {
      const targetDate = dayjs(filterValues[0]);
      if (!targetDate.isValid()) return true;
      return sessionDate.isAfter(targetDate);
    }
    case "between": {
      const startDate = dayjs(filterValues[0]);
      const endDate = dayjs(filterValues[1]);
      if (!startDate.isValid() || !endDate.isValid()) return true;
      return sessionDate.isAfter(startDate) && sessionDate.isBefore(endDate);
    }
    case "in_last_n_days": {
      const days = parseInt(filterValues[0], 10);
      if (isNaN(days)) return true;
      const threshold = dayjs().subtract(days, "day");
      return sessionDate.isAfter(threshold);
    }
    case "in_last_n_hours": {
      const hours = parseInt(filterValues[0], 10);
      if (isNaN(hours)) return true;
      const threshold = dayjs().subtract(hours, "hour");
      return sessionDate.isAfter(threshold);
    }
    default:
      return true;
  }
}

function matchStringWithFilter(
  value: string,
  operator: string,
  filterValues: string[]
): boolean {
  switch (operator) {
    case "equals":
      return filterValues.some((v) => v.toLowerCase() === value.toLowerCase());
    case "not_equals":
      return filterValues.every((v) => v.toLowerCase() !== value.toLowerCase());
    case "contains":
      return filterValues.some((v) =>
        value.toLowerCase().includes(v.toLowerCase())
      );
    case "not_contains":
      return filterValues.every(
        (v) => !value.toLowerCase().includes(v.toLowerCase())
      );
    case "starts_with":
      return filterValues.some((v) =>
        value.toLowerCase().startsWith(v.toLowerCase())
      );
    case "ends_with":
      return filterValues.some((v) =>
        value.toLowerCase().endsWith(v.toLowerCase())
      );
    case "in":
      return filterValues.some((v) => v.toLowerCase() === value.toLowerCase());
    case "not_in":
      return filterValues.every((v) => v.toLowerCase() !== value.toLowerCase());
    default:
      return true;
  }
}

/** Matches an array against the filter; true when ANY item matches. */
function matchArrayWithFilter(
  values: string[],
  operator: string,
  filterValues: string[]
): boolean {
  if (values.length === 0) return operator.includes("not");

  switch (operator) {
    case "equals":
      return values.some((v) =>
        filterValues.some((fv) => fv.toLowerCase() === v.toLowerCase())
      );
    case "not_equals":
      return values.every((v) =>
        filterValues.every((fv) => fv.toLowerCase() !== v.toLowerCase())
      );
    case "contains":
      return values.some((v) =>
        filterValues.some((fv) => v.toLowerCase().includes(fv.toLowerCase()))
      );
    case "not_contains":
      return values.every((v) =>
        filterValues.every((fv) => !v.toLowerCase().includes(fv.toLowerCase()))
      );
    case "starts_with":
      return values.some((v) =>
        filterValues.some((fv) => v.toLowerCase().startsWith(fv.toLowerCase()))
      );
    case "ends_with":
      return values.some((v) =>
        filterValues.some((fv) => v.toLowerCase().endsWith(fv.toLowerCase()))
      );
    case "in":
      return values.some((v) =>
        filterValues.some((fv) => fv.toLowerCase() === v.toLowerCase())
      );
    case "not_in":
      return values.every((v) =>
        filterValues.every((fv) => fv.toLowerCase() !== v.toLowerCase())
      );
    default:
      return true;
  }
}

function applyFilter(
  session: AggregatedSessionV2,
  filter: SegmentFilter
): boolean {
  const filterValue = Array.isArray(filter.value)
    ? filter.value.map(String)
    : [String(filter.value)];

  // Handle timestamp filters
  if (filter.type === "session_start") {
    return matchDateWithFilter(
      session.sessionStart,
      filter.operator,
      filterValue
    );
  }
  if (filter.type === "session_end") {
    return matchDateWithFilter(
      session.sessionEnd,
      filter.operator,
      filterValue
    );
  }

  if (filter.type === "session_duration") {
    const duration = getSessionDuration(session);
    return matchNumericWithFilter(duration, filter.operator, filterValue);
  }
  if (filter.type === "pages_viewed") {
    const pagesViewed = getPageViewsCount(session);
    return matchNumericWithFilter(pagesViewed, filter.operator, filterValue);
  }
  if (filter.type === "screen_width") {
    const screenWidth = session.screenWidth || 0;
    return matchNumericWithFilter(screenWidth, filter.operator, filterValue);
  }
  if (filter.type === "screen_height") {
    const screenHeight = session.screenHeight || 0;
    return matchNumericWithFilter(screenHeight, filter.operator, filterValue);
  }

  // Handle boolean filters
  if (filter.type === "signed_up") {
    const boolValue =
      filterValue[0]?.toLowerCase() === "true" || filterValue[0] === "yes";
    if (filter.operator === "equals") return session.signedUp === boolValue;
    if (filter.operator === "not_equals") return session.signedUp !== boolValue;
    return true;
  }
  if (filter.type === "contacted") {
    const boolValue =
      filterValue[0]?.toLowerCase() === "true" || filterValue[0] === "yes";
    if (filter.operator === "equals") return session.contacted === boolValue;
    if (filter.operator === "not_equals")
      return session.contacted !== boolValue;
    return true;
  }

  // Handle pageSessions-based filters
  if (filter.type === "page_path") {
    const pagePaths = session.pageSessions.map((ps) => ps.pagePath);
    return matchArrayWithFilter(pagePaths, filter.operator, filterValue);
  }
  if (filter.type === "experiment") {
    const experiments = session.pageSessions
      .map((ps) => ps.experimentId)
      .filter((e): e is string => Boolean(e));
    return matchArrayWithFilter(experiments, filter.operator, filterValue);
  }
  if (filter.type === "variant") {
    const variants = session.pageSessions
      .map((ps) => ps.variantId)
      .filter((v): v is string => Boolean(v));
    return matchArrayWithFilter(variants, filter.operator, filterValue);
  }

  // Handle string filters
  let value: string | undefined;

  switch (filter.type) {
    case "country":
      value = session.country;
      break;
    case "region":
      value = session.region;
      break;
    case "city":
      value = session.city;
      break;
    case "timezone":
      value = session.timezone;
      break;
    case "persona":
      value = session.persona || undefined;
      break;
    case "email":
      value = session.email;
      break;
    case "user_agent":
      value = session.userAgent;
      break;
    case "device":
      if (session.screenWidth) {
        if (session.screenWidth < 768) value = "mobile";
        else if (session.screenWidth < 1024) value = "tablet";
        else value = "desktop";
      }
      break;
    case "screen_size":
      if (session.screenWidth && session.screenHeight) {
        value = `${session.screenWidth}x${session.screenHeight}`;
      }
      break;
    case "landing_page":
      value = getPageFlow(session)[0];
      break;
    case "referrer":
      value = session.referrer;
      break;
    case "source":
      value = session.linkSource;
      break;
    default:
      return true;
  }

  if (!value) return filter.operator.includes("not");
  return matchStringWithFilter(value, filter.operator, filterValue);
}

export function filterSessionsBySegment(
  sessions: AggregatedSessionV2[],
  segment: Segment
): AggregatedSessionV2[] {
  if (segment.filters.length === 0) {
    return sessions;
  }

  return sessions.filter((session) => {
    return segment.filters.every((filter) => applyFilter(session, filter));
  });
}

function median(arr: number[]): number {
  if (arr.length === 0) return 0;
  const sorted = [...arr].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 !== 0
    ? sorted[mid]
    : (sorted[mid - 1] + sorted[mid]) / 2;
}

export function calculateKPIsFromSessions(
  sessions: AggregatedSessionV2[],
  comparisonSessions: AggregatedSessionV2[] | null,
  ctaType: string
): KPIMetric[] {
  if (sessions.length === 0) {
    return [];
  }

  // Current period metrics
  const totalSessions = sessions.length;
  const signups = sessions.filter((s) => s.signedUp).length;
  const contacts = sessions.filter((s) => s.contacted).length;

  // Median pages per session
  const pagesPerSession = sessions.map((s) => getPageViewsCount(s));
  const medianPagesPerSession = median(pagesPerSession);

  // Median session duration
  const durations = sessions.map((s) => getSessionDuration(s));
  const medianDuration = median(durations);

  // Engaged sessions (>1 page AND >10s duration)
  const engagedSessions = sessions.filter(
    (s) => getPageViewsCount(s) > 1 && getSessionDuration(s) > 10
  ).length;
  const engagedRate = totalSessions > 0 ? engagedSessions / totalSessions : 0;

  // Previous period metrics
  let prevSessions = 0;
  let prevSignups = 0;
  let prevContacts = 0;
  let prevMedianPages = 0;
  let prevMedianDuration = 0;
  let prevEngagedRate = 0;

  if (comparisonSessions && comparisonSessions.length > 0) {
    prevSessions = comparisonSessions.length;
    prevSignups = comparisonSessions.filter((s) => s.signedUp).length;
    prevContacts = comparisonSessions.filter((s) => s.contacted).length;
    prevMedianPages = median(
      comparisonSessions.map((s) => getPageViewsCount(s))
    );
    prevMedianDuration = median(
      comparisonSessions.map((s) => getSessionDuration(s))
    );
    const prevEngaged = comparisonSessions.filter(
      (s) => getPageViewsCount(s) > 1 && getSessionDuration(s) > 10
    ).length;
    prevEngagedRate = prevSessions > 0 ? prevEngaged / prevSessions : 0;
  }

  // Calculate change percentages
  const calcChange = (curr: number, prev: number): number | undefined => {
    if (!comparisonSessions || prev === 0) return undefined;
    return ((curr - prev) / prev) * 100;
  };

  const getChangeDirection = (
    change: number | undefined
  ): "up" | "down" | "neutral" | undefined => {
    if (change === undefined) return undefined;
    if (change > 1) return "up";
    if (change < -1) return "down";
    return "neutral";
  };

  let conversions = signups;
  let prevConversions = prevSignups;
  let conversionLabel = "Signups";

  if (ctaType === "contact_submit") {
    conversions = contacts;
    prevConversions = prevContacts;
    conversionLabel = "Contacts";
  } else if (ctaType === "cta_click") {
    conversions = signups + contacts;
    prevConversions = prevSignups + prevContacts;
    conversionLabel = "CTA Conversions";
  }

  const conversionRate = totalSessions > 0 ? conversions / totalSessions : 0;
  const prevConversionRate =
    prevSessions > 0 ? prevConversions / prevSessions : 0;

  const sessionsChange = calcChange(totalSessions, prevSessions);
  const engagedChange = calcChange(engagedRate, prevEngagedRate);
  const pagesChange = calcChange(medianPagesPerSession, prevMedianPages);
  const durationChange = calcChange(medianDuration, prevMedianDuration);
  const conversionsChange = calcChange(conversions, prevConversions);
  const convRateChange = calcChange(conversionRate, prevConversionRate);

  return [
    {
      id: "sessions",
      label: "Sessions",
      value: totalSessions,
      format: "number",
      change: sessionsChange,
      changeDirection: getChangeDirection(sessionsChange),
      description: "Total unique sessions",
    },
    {
      id: "engaged_rate",
      label: "Engaged Session Rate",
      value: engagedRate,
      format: "percentage",
      change: engagedChange,
      changeDirection: getChangeDirection(engagedChange),
      description: "Sessions with >1 page & >10s",
    },
    {
      id: "pages_per_session",
      label: "Median Pages / Session",
      value: medianPagesPerSession,
      format: "number",
      change: pagesChange,
      changeDirection: getChangeDirection(pagesChange),
      description: "Median pages viewed",
    },
    {
      id: "session_duration",
      label: "Median Session Duration",
      value: medianDuration,
      format: "duration",
      change: durationChange,
      changeDirection: getChangeDirection(durationChange),
      description: "Median time on site",
    },
    {
      id: "conversions",
      label: conversionLabel,
      value: conversions,
      format: "number",
      change: conversionsChange,
      changeDirection: getChangeDirection(conversionsChange),
      description: "Total conversions",
    },
    {
      id: "conversion_rate",
      label: "Conversion Rate",
      value: conversionRate,
      format: "percentage",
      change: convRateChange,
      changeDirection: getChangeDirection(convRateChange),
      description: "Sessions that converted",
    },
  ];
}

export function extractTimeSeriesFromSessions(
  sessions: AggregatedSessionV2[],
  comparisonSessions: AggregatedSessionV2[] | null,
  metric: "sessions" | "conversions",
  granularity: "day" | "week" | "month"
): TimeSeriesDataPoint[] {
  // Group sessions by date
  const groupByDate = (
    sessionList: AggregatedSessionV2[]
  ): Map<string, AggregatedSessionV2[]> => {
    const groups = new Map<string, AggregatedSessionV2[]>();

    sessionList.forEach((session) => {
      // sessionStart is epoch ms, convert to dayjs
      const date = dayjs(session.sessionStart);
      let key: string;

      switch (granularity) {
        case "week":
          key = date.startOf("week").format("YYYY-MM-DD");
          break;
        case "month":
          key = date.startOf("month").format("YYYY-MM-DD");
          break;
        default:
          key = date.format("YYYY-MM-DD");
      }

      if (!groups.has(key)) {
        groups.set(key, []);
      }
      groups.get(key)!.push(session);
    });

    return groups;
  };

  const currentGroups = groupByDate(sessions);
  const comparisonGroups = comparisonSessions
    ? groupByDate(comparisonSessions)
    : null;

  const currentEntries = Array.from(currentGroups.entries())
    .map(([timestamp, groupSessions]) => {
      const value =
        metric === "sessions"
          ? groupSessions.length
          : groupSessions.filter((s) => s.signedUp || s.contacted).length;
      return { timestamp, value };
    })
    .sort(
      (a, b) =>
        new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    );

  // Map comparison values by relative index
  const comparisonEntries = comparisonGroups
    ? Array.from(comparisonGroups.entries())
        .map(([timestamp, groupSessions]) => {
          const value =
            metric === "sessions"
              ? groupSessions.length
              : groupSessions.filter((s) => s.signedUp || s.contacted).length;
          return { timestamp, value };
        })
        .sort(
          (a, b) =>
            new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
        )
    : null;

  return currentEntries.map((entry, index) => ({
    timestamp: entry.timestamp,
    value: entry.value,
    comparisonValue: comparisonEntries?.[index]?.value,
  }));
}

export function calculateWhatChangedFromSessions(
  sessions: AggregatedSessionV2[],
  comparisonSessions: AggregatedSessionV2[] | null
): WhatChangedItem[] {
  const changes: WhatChangedItem[] = [];

  // Count by country
  const countByCountry = (
    list: AggregatedSessionV2[]
  ): Record<string, number> => {
    const counts: Record<string, number> = {};
    list.forEach((s) => {
      if (s.country) {
        counts[s.country] = (counts[s.country] || 0) + 1;
      }
    });
    return counts;
  };

  // Count by landing page
  const countByLandingPage = (
    list: AggregatedSessionV2[]
  ): Record<string, number> => {
    const counts: Record<string, number> = {};
    list.forEach((s) => {
      const landing = getPageFlow(s)[0];
      if (landing) {
        counts[landing] = (counts[landing] || 0) + 1;
      }
    });
    return counts;
  };

  const currentCountries = countByCountry(sessions);
  const currentPages = countByLandingPage(sessions);

  if (!comparisonSessions || comparisonSessions.length === 0) {
    // Just show top items without comparison
    Object.entries(currentCountries)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 3)
      .forEach(([country, count]) => {
        if (count > 3) {
          changes.push({
            category: "source",
            item: country,
            metricName: "Sessions",
            previousValue: 0,
            currentValue: count,
            changePercentage: 0,
          });
        }
      });

    return changes.slice(0, 6);
  }

  const prevCountries = countByCountry(comparisonSessions);
  const prevPages = countByLandingPage(comparisonSessions);

  // Compare countries
  const allCountries = new Set([
    ...Object.keys(currentCountries),
    ...Object.keys(prevCountries),
  ]);

  allCountries.forEach((country) => {
    const curr = currentCountries[country] || 0;
    const prev = prevCountries[country] || 0;

    if (prev > 0 && curr > 3) {
      const change = ((curr - prev) / prev) * 100;
      if (Math.abs(change) > 10) {
        changes.push({
          category: "source",
          item: country,
          metricName: "Sessions",
          previousValue: prev,
          currentValue: curr,
          changePercentage: change,
        });
      }
    }
  });

  // Compare landing pages
  const allPages = new Set([
    ...Object.keys(currentPages),
    ...Object.keys(prevPages),
  ]);

  allPages.forEach((page) => {
    const curr = currentPages[page] || 0;
    const prev = prevPages[page] || 0;

    if (prev > 0 && curr > 3) {
      const change = ((curr - prev) / prev) * 100;
      if (Math.abs(change) > 15) {
        changes.push({
          category: "landing_page",
          item: page,
          metricName: "Sessions",
          previousValue: prev,
          currentValue: curr,
          changePercentage: change,
        });
      }
    }
  });

  return changes
    .sort((a, b) => Math.abs(b.changePercentage) - Math.abs(a.changePercentage))
    .slice(0, 6);
}
